import { useEffect, useMemo, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import {
  miabApi,
  type MiabSurveyData,
  type MiabQuestion,
  type MiabAnswerInput,
} from '../api/miab';
import { useAuth } from '../hooks/useAuth';
import { showToast } from '../components/Toast';

function fmtDate(s: string | null): string {
  if (!s) return '';
  try {
    return new Date(s).toLocaleDateString(undefined, { month: 'long', day: 'numeric', year: 'numeric' });
  } catch {
    return s;
  }
}

// Per-question working state.
type AnswerState = { optionIds: number[]; ratings: Record<number, number>; text: string };
const emptyAnswer = (): AnswerState => ({ optionIds: [], ratings: {}, text: '' });

/**
 * The franchisee-facing MIAB survey. Renders a survey's questions
 * (single-choice, multi-select, 1-5 rating, free text) and submits one
 * response per AF. Reached at /miab (whatever is open) or the stable
 * /miab/<slug> link emailed out.
 */
export default function MiabSurveyPage() {
  const { slug } = useParams<{ slug?: string }>();
  const [searchParams] = useSearchParams();
  const { user } = useAuth();
  const queryClient = useQueryClient();

  // Admin preview: render exactly what a franchisee sees, regardless of role or
  // window state. Nothing is saved. Admin-gated.
  const isPreview = searchParams.get('preview') === '1' && user?.role === 'admin';

  const query = useQuery<MiabSurveyData | null>({
    queryKey: ['miab', slug ?? 'active'],
    queryFn: () => (slug ? miabApi.bySlug(slug) : miabApi.active()),
  });

  const data = query.data ?? null;
  const survey = data?.survey ?? null;
  const questions = useMemo(() => data?.questions ?? [], [data]);

  const afs = user?.afNumbers ?? [];
  const [af, setAf] = useState<string>('');
  useEffect(() => {
    if (!af && afs.length > 0) setAf(afs[0]!);
  }, [afs, af]);

  const existing = useMemo(
    () => data?.responses.find((r) => r.af_number === af) ?? null,
    [data, af],
  );

  const [answers, setAnswers] = useState<Record<number, AnswerState>>({});

  // Prefill from the selected AF's saved response whenever AF or survey changes.
  useEffect(() => {
    const next: Record<number, AnswerState> = {};
    for (const q of questions) next[q.id] = emptyAnswer();
    for (const a of existing?.answers ?? []) {
      const slot = next[a.questionId];
      if (!slot) continue;
      if (a.text != null) slot.text = a.text;
      else if (a.rating != null && a.optionId != null) slot.ratings[a.optionId] = a.rating;
      else if (a.optionId != null) slot.optionIds.push(a.optionId);
    }
    setAnswers(next);
  }, [existing, questions]);

  const isOpen = survey?.state === 'open';
  const canRespond = Boolean(data?.canRespond) && isOpen;
  const interactive = canRespond || isPreview;

  const submit = useMutation({
    mutationFn: () => {
      const payload: MiabAnswerInput[] = questions.map((q) => {
        const s = answers[q.id] ?? emptyAnswer();
        if (q.qtype === 'text') return { questionId: q.id, text: s.text };
        if (q.qtype === 'rating') return { questionId: q.id, ratings: s.ratings };
        return { questionId: q.id, optionIds: s.optionIds };
      });
      return miabApi.respond(survey!.slug, af, payload);
    },
    onSuccess: () => {
      showToast(existing ? 'Your answers were updated.' : 'Thanks — your response was recorded.');
      queryClient.invalidateQueries({ queryKey: ['miab'] });
    },
    onError: (e: Error) => showToast(e.message, { variant: 'error' }),
  });

  function setSlot(qId: number, patch: Partial<AnswerState>) {
    setAnswers((prev) => ({ ...prev, [qId]: { ...(prev[qId] ?? emptyAnswer()), ...patch } }));
  }
  function toggleOption(q: MiabQuestion, optId: number) {
    const slot = answers[q.id] ?? emptyAnswer();
    if (q.qtype === 'single') {
      setSlot(q.id, { optionIds: slot.optionIds[0] === optId ? [] : [optId] });
    } else {
      const has = slot.optionIds.includes(optId);
      setSlot(q.id, {
        optionIds: has ? slot.optionIds.filter((x) => x !== optId) : [...slot.optionIds, optId],
      });
    }
  }

  if (query.isLoading) {
    return (
      <div className="auth-loading">
        <div className="spinner" />
        <span>Loading…</span>
      </div>
    );
  }

  if (query.isError) {
    return (
      <div className="miab-page">
        <div className="miab-empty">
          <h2>Survey unavailable</h2>
          <p>{(query.error as Error).message}</p>
          <Link to="/" className="btn">Back to dashboard</Link>
        </div>
      </div>
    );
  }

  if (!survey) {
    return (
      <div className="miab-page">
        <div className="miab-empty">
          <h2>No survey is open right now</h2>
          <p>
            Marketing in a Box surveys open about two weeks before each ordering
            window. We'll email you when the next one is live.
          </p>
          <Link to="/" className="btn">Back to dashboard</Link>
        </div>
      </div>
    );
  }

  const stateWord =
    survey.state === 'open' ? 'open now' : survey.state === 'upcoming' ? 'not open yet' : 'closed';

  return (
    <div className="miab-page">
      {isPreview && (
        <div className="miab-preview-bar" role="status">
          <strong>Preview</strong>
          <span>
            This is exactly what franchisees see. Nothing you enter here is saved.
            {' '}This survey is {survey.is_published ? stateWord : 'still a draft'}.
          </span>
          <Link to="/miab/admin" className="miab-preview-exit">Back to surveys</Link>
        </div>
      )}

      <header className="miab-header">
        <Link to="/" className="miab-back">← Dashboard</Link>
        <h1>{survey.title}</h1>
        {survey.intro && <p className="miab-intro">{survey.intro}</p>}
        <div className="miab-meta">
          <span className="miab-pill">{survey.box_label} box</span>
          <span>Survey closes <strong>{fmtDate(survey.closes_at)}</strong></span>
          {survey.order_window_start && (
            <span>
              Ordering opens <strong>{fmtDate(survey.order_window_start)}</strong> at{' '}
              <a href="https://www.theamadastore.com" target="_blank" rel="noreferrer">theamadastore.com</a>
            </span>
          )}
        </div>

        {survey.state === 'upcoming' && !isPreview && (
          <div className="miab-notice">
            This survey opens {fmtDate(survey.opens_at)}. You can look, but you can't submit yet.
          </div>
        )}
        {survey.state === 'closed' && !isPreview && (
          <div className="miab-notice">
            This survey closed {fmtDate(survey.closes_at)}. Thanks to everyone who responded.
          </div>
        )}
        {isOpen && !data?.canRespond && !isPreview && (
          <div className="miab-notice">
            You're viewing this as {user?.role === 'admin' ? 'an admin' : 'an FBC'}. Only
            franchisees can submit answers, so the results reflect the field.
          </div>
        )}
      </header>

      {interactive && afs.length > 1 && (
        <div className="miab-af-picker">
          <label htmlFor="miab-af">Answering for</label>
          <select id="miab-af" value={af} onChange={(e) => setAf(e.target.value)}>
            {afs.map((a) => <option key={a} value={a}>{a}</option>)}
          </select>
          <span className="miab-af-hint">Each location gets its own answer.</span>
        </div>
      )}

      {existing && interactive && (
        <div className="miab-counter" role="status">
          <span className="miab-saved-tag">You've already answered — edits are saved on submit.</span>
        </div>
      )}

      <div className="miab-questions">
        {questions.map((q, idx) => (
          <QuestionBlock
            key={q.id}
            q={q}
            index={idx + 1}
            state={answers[q.id] ?? emptyAnswer()}
            interactive={interactive}
            onToggleOption={(optId) => toggleOption(q, optId)}
            onRate={(optId, val) =>
              setSlot(q.id, { ratings: { ...(answers[q.id]?.ratings ?? {}), [optId]: val } })
            }
            onText={(t) => setSlot(q.id, { text: t })}
          />
        ))}
      </div>

      {interactive && (
        <div className="miab-submit">
          <button
            className="btn btn-primary"
            disabled={isPreview || submit.isPending || !af}
            onClick={() => {
              if (isPreview) return;
              submit.mutate();
            }}
          >
            {submit.isPending ? 'Saving…' : existing ? 'Update my response' : 'Submit my response'}
          </button>
          {isPreview && (
            <small className="miab-preview-note">
              Submitting is disabled in preview — a franchisee would be able to click this.
            </small>
          )}
        </div>
      )}
    </div>
  );
}

function QuestionBlock({
  q,
  index,
  state,
  interactive,
  onToggleOption,
  onRate,
  onText,
}: {
  q: MiabQuestion;
  index: number;
  state: AnswerState;
  interactive: boolean;
  onToggleOption: (optId: number) => void;
  onRate: (optId: number, val: number) => void;
  onText: (t: string) => void;
}) {
  const ratingMin = q.rating_min ?? 1;
  const ratingMax = q.rating_max ?? 5;
  const scale = Array.from({ length: ratingMax - ratingMin + 1 }, (_, i) => ratingMin + i);
  // Keep these in sync with .miab-rating-dot / .miab-rating-scale in global.css.
  // The legend track is sized to the exact scale width so the min/max labels
  // anchor over the first and last rating buttons instead of the whole row.
  const RATING_DOT_PX = 38;
  const RATING_GAP_PX = 8;
  const scaleWidth = scale.length * RATING_DOT_PX + (scale.length - 1) * RATING_GAP_PX;

  return (
    <section className="miab-q">
      <h2 className="miab-q-prompt">
        <span className="miab-q-num">{index}.</span> {q.prompt}
        {q.required === 1 && <span className="miab-q-req" aria-label="required"> *</span>}
      </h2>
      {q.help_text && <p className="miab-q-help">{q.help_text}</p>}

      {/* single / multi choice */}
      {(q.qtype === 'single' || q.qtype === 'multi') && (
        <ul className="miab-choices">
          {q.options.map((o) => {
            const checked = state.optionIds.includes(o.id);
            return (
              <li key={o.id}>
                <label className={`miab-choice${checked ? ' is-checked' : ''}`}>
                  <input
                    type={q.qtype === 'single' ? 'radio' : 'checkbox'}
                    name={`q-${q.id}`}
                    checked={checked}
                    disabled={!interactive}
                    onChange={() => onToggleOption(o.id)}
                  />
                  <span>{o.label}</span>
                </label>
              </li>
            );
          })}
        </ul>
      )}

      {/* rating matrix with item image */}
      {q.qtype === 'rating' && (
        <div className="miab-rating-table">
          <div className="miab-rating-legend">
            <span className="miab-rating-legend-track" style={{ width: scaleWidth }}>
              <span>{q.rating_min_label ?? ''}</span>
              <span>{q.rating_max_label ?? ''}</span>
            </span>
          </div>
          {q.options.map((o) => (
            <div className="miab-rating-row" key={o.id}>
              <div className="miab-rating-item">
                {o.image_url && <img src={o.image_url} alt="" loading="lazy" />}
                <div>
                  <div className="miab-rating-name">{o.label}</div>
                </div>
              </div>
              <div className="miab-rating-scale" role="radiogroup" aria-label={o.label}>
                {scale.map((n) => {
                  const on = state.ratings[o.id] === n;
                  return (
                    <button
                      key={n}
                      type="button"
                      className={`miab-rating-dot${on ? ' is-on' : ''}`}
                      aria-pressed={on}
                      disabled={!interactive}
                      onClick={() => onRate(o.id, n)}
                    >
                      {n}
                    </button>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* free text */}
      {q.qtype === 'text' && (
        <div className="miab-textq">
          <textarea
            rows={4}
            maxLength={q.max_length ?? undefined}
            value={state.text}
            disabled={!interactive}
            onChange={(e) => onText(e.target.value)}
            placeholder="Your answer"
          />
          {q.max_length && (
            <div className="miab-textq-count">{state.text.length} / {q.max_length}</div>
          )}
        </div>
      )}
    </section>
  );
}
